import React, { createContext, useContext } from 'react';
import { cn } from '@/lib/utils';
import SiteContainer from '../components/site/SiteContainer';
import Eyebrow from '../components/site/Eyebrow';
import Heading from '../components/site/Heading';
import Button from '../components/site/Button';

/**
 * States for route pages that load on demand (React.lazy in routes/public.jsx and
 * routes/lazyPortalPage.jsx), in the Sept 2026 redesign's look. PublicLayout renders
 * PageLoading as the Suspense fallback around its <Outlet/>, so the Header, Footer and
 * bottom nav stay on screen while a page's code downloads. Every lazy page is a
 * SLOT_MODEL page, so these states only ever show with the flag on.
 */

/**
 * True inside the site chrome (PublicLayout: fixed site header above the page), false
 * when a page renders bare (portal routes). Shared pages that paint their own <main>
 * (NotFound, the page states) read it to clear the fixed header only when there is one.
 */
export const SiteChromeContext = createContext(false);

/** Whether the calling page renders under the site chrome (see SiteChromeContext). */
export const useSiteChrome = () => useContext(SiteChromeContext);

/**
 * Top padding that clears the fixed site header (the package's `.page-main`: 110px below
 * the 800px nav breakpoint, 132px from it). Bare pages get a plain top margin.
 */
export const SITE_HEADER_OFFSET = 'pt-[6.875rem] nav:pt-[8.25rem]';
const BARE_OFFSET = 'pt-10 nav:pt-14';

const BLOCK = 'bg-navy/10 motion-safe:animate-pulse';

/**
 * Page-sized placeholder shaped like a redesigned page (eyebrow, two-line title, lede,
 * button, three cards), on the paper canvas. Announced once, politely ("Loading the
 * page…"); the shapes are decorative. It never says "nothing found": the page decides
 * that once its data is in.
 */
export function PageLoading() {
  const inChrome = useSiteChrome();
  return (
    <main
      className={cn('site-light min-h-[70vh] text-navy', inChrome ? cn('site-paper', SITE_HEADER_OFFSET) : cn('bg-brand-paper', BARE_OFFSET))}
      data-testid="page-loading"
    >
      <SiteContainer className="pb-section">
        <div role="status" aria-live="polite">
          <span className="sr-only">Loading the page…</span>
          <div aria-hidden="true" className="max-w-[46rem]">
            <div className={cn('h-3 w-44', BLOCK)} />
            <div className={cn('mt-6 h-11 w-full nav:h-16', BLOCK)} />
            <div className={cn('mt-3 h-11 w-3/4 nav:h-16', BLOCK)} />
            <div className={cn('mt-8 h-4 w-11/12 max-w-[36.875rem]', BLOCK)} />
            <div className={cn('mt-2.5 h-4 w-2/3 max-w-[28rem]', BLOCK)} />
            <div className="mt-8 h-[3.125rem] w-56 border border-navy/15 bg-brand-sheet shadow-site nav:h-[3.25rem]" />
          </div>
          <div aria-hidden="true" className="mt-14 grid gap-6 md:grid-cols-3">
            {[0, 1, 2].map((i) => (
              <div key={i} className="h-44 border border-brand-rule bg-brand-sheet p-6 shadow-site-card">
                <div className={cn('size-[2.8125rem] rounded-full', BLOCK)} />
                <div className={cn('mt-6 h-5 w-2/3', BLOCK)} />
                <div className={cn('mt-3 h-3.5 w-5/6', BLOCK)} />
              </div>
            ))}
          </div>
        </div>
      </SiteContainer>
    </main>
  );
}

/**
 * Shown in place of a page whose code failed to download (offline, or a deploy replaced
 * the chunk). One h1 inside an alert, and a button that reloads the page (the reload
 * fetches the current build).
 */
export function PageLoadError() {
  const inChrome = useSiteChrome();
  return (
    <main
      className={cn('site-light min-h-[70vh] text-navy', inChrome ? cn('site-paper', SITE_HEADER_OFFSET) : cn('bg-brand-paper', BARE_OFFSET))}
    >
      <SiteContainer width="narrow" className="pb-section">
        <section aria-labelledby="page-load-error-title">
          <div role="alert">
            <Eyebrow tone="rust">Something went wrong</Eyebrow>
            <Heading level={1} id="page-load-error-title" className="mt-5">
              This page didn&apos;t load
            </Heading>
            <p className="mt-6 max-w-[36.875rem] text-[1.0625rem] leading-[1.65] text-brand-muted nav:text-[1.1875rem]">
              Check your connection and try again.
            </p>
          </div>
          <Button className="mt-8" arrow="↺" onClick={() => window.location.reload()}>
            Reload the page
          </Button>
        </section>
      </SiteContainer>
    </main>
  );
}
